import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faVolumeUp, 
  faEnvelope, 
  faMobile,
  faSms,
  faToggleOn,
  faToggleOff,
  faCog,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { 
  Card, 
  Form, 
  Row, 
  Col, 
  Button, 
  Alert,
  Badge,
  InputGroup
} from '@themesberg/react-bootstrap';
import { toast } from 'react-hot-toast';
import { NOTIFICATION_CATEGORIES } from '../constants/notificationTypes';
import notificationSoundService from '../services/notificationSoundService';
import { postApi, fetchApi } from '../redux/api';

const NotificationPreferences = ({ user }) => {
  const [preferences, setPreferences] = useState({
    soundEnabled: true,
    emailEnabled: true,
    smsEnabled: false,
    pushEnabled: false,
    volume: 0.7,
    categories: {
      [NOTIFICATION_CATEGORIES.APPROVAL]: { enabled: true, priority: 'high' },
      [NOTIFICATION_CATEGORIES.RELEASE]: { enabled: true, priority: 'high' },
      [NOTIFICATION_CATEGORIES.PAYMENT]: { enabled: true, priority: 'medium' },
      [NOTIFICATION_CATEGORIES.SYSTEM]: { enabled: true, priority: 'low' },
      [NOTIFICATION_CATEGORIES.REMINDER]: { enabled: true, priority: 'medium' }
    },
    quietHours: {
      enabled: false,
      start: '18:00',
      end: '08:00'
    },
    frequency: 'immediate' // immediate, hourly, daily
  });

  const [loading, setLoading] = useState(false);
  const [testingSound, setTestingSound] = useState(false);

  // Load preferences from backend + localStorage on component mount
  useEffect(() => {
    const savedLocal = localStorage.getItem('notificationPreferences');
    if (savedLocal) {
      try {
        const parsed = JSON.parse(savedLocal);
        setPreferences(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      }
    }

    // Load from backend (overrides local)
    if (user?.username) {
      fetchApi('api/notification-preferences')
        .then((data) => {
          if (data.success && data.preferences) {
            setPreferences(prev => ({ ...prev, ...data.preferences }));
          }
        })
        .catch(() => {
          console.log('Backend preferences not available, using local');
        });
    }
  }, [user]);

  // Save preferences to localStorage AND backend
  const savePreferences = async () => {
    setLoading(true);
    try {
      localStorage.setItem('notificationPreferences', JSON.stringify(preferences));

      notificationSoundService.setEnabled(preferences.soundEnabled);
      notificationSoundService.setVolume(preferences.volume);

      // Sync to backend
      await postApi('api/notification-preferences', preferences);

      toast.success('Notification preferences saved successfully!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key, subKey = null) => {
    setPreferences(prev => {
      if (subKey) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            [subKey]: !prev[key][subKey]
          }
        };
      }
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  const handleCategoryChange = (category, field, value) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          [field]: value
        }
      }
    }));
  };

  const handleVolumeChange = (volume) => {
    setPreferences(prev => ({ ...prev, volume }));
    notificationSoundService.setVolume(volume);
  };

  const testSound = async () => {
    setTestingSound(true);
    try {
      notificationSoundService.playSound('info');
      toast.success('Test sound played!');
    } catch (error) {
      toast.error('Could not play test sound');
    } finally {
      setTimeout(() => setTestingSound(false), 1000);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return faExclamationTriangle;
      case 'medium': return faExclamationCircle;
      case 'low': return faInfoCircle;
      default: return faInfoCircle;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  return (
    <Card className="border-0 shadow">
      <Card.Header className="bg-primary text-white">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faBell} className="me-2" />
          <h5 className="mb-0">Notification Preferences</h5>
        </div>
      </Card.Header>
      
      <Card.Body>
        {/* General Settings */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <FontAwesomeIcon icon={faCog} className="me-2" />
            General Settings
          </h6>
          
          <Row>
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                <div>
                  <FontAwesomeIcon icon={faVolumeUp} className="me-2 text-primary" />
                  <strong>Sound Notifications</strong>
                  <div className="small text-muted">Play sounds for new notifications</div>
                </div>
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => handleToggle('soundEnabled')}
                >
                  <FontAwesomeIcon 
                    icon={preferences.soundEnabled ? faToggleOn : faToggleOff}
                    size="lg"
                    className={preferences.soundEnabled ? 'text-success' : 'text-muted'}
                  />
                </Button>
              </div>
            </Col>
            
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                <div>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                  <strong>Email Notifications</strong>
                  <div className="small text-muted">Receive notifications via email</div>
                </div>
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => handleToggle('emailEnabled')}
                >
                  <FontAwesomeIcon 
                    icon={preferences.emailEnabled ? faToggleOn : faToggleOff}
                    size="lg"
                    className={preferences.emailEnabled ? 'text-success' : 'text-muted'}
                  />
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                <div>
                  <FontAwesomeIcon icon={faSms} className="me-2 text-primary" />
                  <strong>SMS Notifications</strong>
                  <div className="small text-muted">Receive notifications via SMS</div>
                </div>
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => handleToggle('smsEnabled')}
                >
                  <FontAwesomeIcon 
                    icon={preferences.smsEnabled ? faToggleOn : faToggleOff}
                    size="lg"
                    className={preferences.smsEnabled ? 'text-success' : 'text-muted'}
                  />
                </Button>
              </div>
            </Col>
          </Row>

          {/* Volume Control */}
          {preferences.soundEnabled && (
            <div className="mb-3">
              <Form.Label className="fw-bold">
                <FontAwesomeIcon icon={faVolumeUp} className="me-2" />
                Volume: {Math.round(preferences.volume * 100)}%
              </Form.Label>
              <div className="d-flex align-items-center">
                <Form.Range
                  min={0}
                  max={1}
                  step={0.1}
                  value={preferences.volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="me-3"
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={testSound}
                  disabled={testingSound}
                >
                  {testingSound ? 'Playing...' : 'Test'}
                </Button>
              </div>
            </div>
          )}

          {/* Notification Frequency */}
          <div className="mb-3">
            <Form.Label className="fw-bold">Notification Frequency</Form.Label>
            <Form.Select
              value={preferences.frequency}
              onChange={(e) => setPreferences(prev => ({ ...prev, frequency: e.target.value }))}
            >
              <option value="immediate">Immediate</option>
              <option value="hourly">Hourly Summary</option>
              <option value="daily">Daily Summary</option>
            </Form.Select>
            <Form.Text className="text-muted">
              How often you want to receive notifications
            </Form.Text>
          </div>
        </div>

        {/* Category Settings */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <FontAwesomeIcon icon={faBell} className="me-2" />
            Notification Categories
          </h6>
          
          {Object.entries(preferences.categories).map(([category, settings]) => (
            <div key={category} className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <strong className="text-capitalize me-2">{category}</strong>
                  <Badge bg={getPriorityColor(settings.priority)} className="me-2">
                    <FontAwesomeIcon icon={getPriorityIcon(settings.priority)} className="me-1" />
                    {settings.priority}
                  </Badge>
                </div>
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => handleCategoryChange(category, 'enabled', !settings.enabled)}
                >
                  <FontAwesomeIcon 
                    icon={settings.enabled ? faToggleOn : faToggleOff}
                    size="lg"
                    className={settings.enabled ? 'text-success' : 'text-muted'}
                  />
                </Button>
              </div>
              
              {settings.enabled && (
                <div>
                  <Form.Label className="small">Priority Level</Form.Label>
                  <Form.Select
                    size="sm"
                    value={settings.priority}
                    onChange={(e) => handleCategoryChange(category, 'priority', e.target.value)}
                  >
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </Form.Select>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quiet Hours */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">Quiet Hours</h6>
          
          <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
            <div>
              <strong>Enable Quiet Hours</strong>
              <div className="small text-muted">Disable notifications during specified hours</div>
            </div>
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleToggle('quietHours', 'enabled')}
            >
              <FontAwesomeIcon 
                icon={preferences.quietHours.enabled ? faToggleOn : faToggleOff}
                size="lg"
                className={preferences.quietHours.enabled ? 'text-success' : 'text-muted'}
              />
            </Button>
          </div>

          {preferences.quietHours.enabled && (
            <Row>
              <Col md={6}>
                <Form.Label className="small">Start Time</Form.Label>
                <Form.Control
                  type="time"
                  value={preferences.quietHours.start}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    quietHours: { ...prev.quietHours, start: e.target.value }
                  }))}
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small">End Time</Form.Label>
                <Form.Control
                  type="time"
                  value={preferences.quietHours.end}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    quietHours: { ...prev.quietHours, end: e.target.value }
                  }))}
                />
              </Col>
            </Row>
          )}
        </div>

        {/* Save Button */}
        <div className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={savePreferences}
            disabled={loading}
            className="px-4"
          >
            {loading ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>

        {/* Info Alert */}
        <Alert variant="info" className="mt-3 mb-0">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          <strong>Note:</strong> These preferences are saved locally on your device. 
          You may need to reconfigure them if you use a different browser or device.
        </Alert>
      </Card.Body>
    </Card>
  );
};

export default NotificationPreferences;